import {Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {formatRating, reviewCountLabel, ReviewSummary} from "../../../../models/review";
import {ReviewService} from "../../../../services/review/review.service";
import {ReviewCardComponent} from "../../../../components/review-card/review-card.component";
import {StarRatingComponent} from "../../../../components/star-rating/star-rating.component";

/** Reviews under a product, and the form to leave one. New reviews appear after the admin approves them. */
@Component({
    selector: 'app-product-reviews',
    standalone: true,
    imports: [ReactiveFormsModule, ReviewCardComponent, StarRatingComponent],
    templateUrl: './product-reviews.component.html'
})
export class ProductReviewsComponent implements OnInit {

    @Input({required: true})
    productId!: number;

    @Input()
    summary: ReviewSummary | null = null;

    /** Reviews shown before "show all" - long lists push the related products far down. */
    readonly initialCount = 4;

    showAll = false;

    showForm = false;

    sending = false;

    sent = false;

    failed = false;

    readonly ratings = [1, 2, 3, 4, 5];

    form = new FormGroup({
        authorName: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.maxLength(60)]}),
        city: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(60)]}),
        rating: new FormControl(0, {nonNullable: true, validators: [Validators.min(1), Validators.max(5)]}),
        text: new FormControl('', {nonNullable: true, validators: [Validators.required, Validators.minLength(10), Validators.maxLength(2000)]}),
        phoneNumber: new FormControl('', {nonNullable: true, validators: [Validators.maxLength(40)]}),
        website: new FormControl('', {nonNullable: true})
    });

    protected readonly formatRating = formatRating;
    protected readonly reviewCountLabel = reviewCountLabel;

    constructor(private readonly reviewService: ReviewService,
                private readonly route: ActivatedRoute) {
    }

    /** The "leave a review" link in the follow-up email adds ?review=1: open the form and go there. */
    ngOnInit(): void {
        if (this.route.snapshot.queryParamMap.has('review')) {
            this.showForm = true;
            setTimeout(() => document.getElementById('reviews')?.scrollIntoView(), 800);
        }
    }

    get visibleReviews() {
        const reviews = this.summary?.reviews ?? [];
        return this.showAll ? reviews : reviews.slice(0, this.initialCount);
    }

    setRating(rating: number): void {
        this.form.controls.rating.setValue(rating);
        this.form.controls.rating.markAsTouched();
    }

    submit(): void {
        if (this.form.invalid || this.sending) {
            this.form.markAllAsTouched();
            return;
        }
        const value = this.form.getRawValue();
        this.sending = true;
        this.failed = false;
        this.reviewService.submit({
            productId: this.productId,
            authorName: value.authorName.trim(),
            city: value.city.trim() || null,
            rating: value.rating,
            text: value.text.trim(),
            phoneNumber: value.phoneNumber.trim() || null,
            website: value.website
        }).subscribe({
            next: () => {
                this.sending = false;
                this.sent = true;
                this.form.reset();
            },
            error: () => {
                this.sending = false;
                this.failed = true;
            }
        });
    }
}
